import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { ClockClockwise, CircleNotch, CheckCircle, XCircle, User } from "@phosphor-icons/react";
import Pagination from "../shared/Pagination";

const API = `${window.location.origin}/api`;

const METHOD_BADGE = {
  POST: "bg-emerald-100 text-emerald-700",
  PUT: "bg-muted text-foreground",
  PATCH: "bg-muted text-foreground",
  DELETE: "bg-red-100 text-red-700",
};

const ROLE_BADGE = {
  admin: "bg-violet-100 text-violet-700",
  mechanic: "bg-amber-100 text-amber-700",
  employee: "bg-cyan-100 text-cyan-700",
  anonymous: "bg-slate-100 text-slate-600",
};

function formatTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AuditLogTab() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState("");
  const [successFilter, setSuccessFilter] = useState("");
  const [limit, setLimit] = useState(200);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return records.slice(start, start + pageSize);
  }, [records, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [records.length, pageSize, roleFilter, successFilter, limit]);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (roleFilter) params.role_filter = roleFilter;
      if (successFilter !== "") params.success = successFilter === "true";
      const { data } = await axios.get(`${API}/audit-log`, { params });
      setRecords(data || []);
    } catch (err) {
      console.error("audit-log fetch error", err);
    } finally {
      setLoading(false);
    }
  }, [roleFilter, successFilter, limit]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  return (
    <div className="space-y-6" data-testid="audit-log-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ClockClockwise className="w-6 h-6 text-violet-600" weight="duotone" />
          <h2 className="font-bold text-xl text-slate-800">
            Aktywność użytkowników
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="input-field text-sm"
            data-testid="audit-role-filter"
          >
            <option value="">Wszystkie role</option>
            <option value="admin">Admin</option>
            <option value="mechanic">Mechanik</option>
            <option value="employee">Pracownik</option>
            <option value="anonymous">Anonim</option>
          </select>
          <select
            value={successFilter}
            onChange={(e) => setSuccessFilter(e.target.value)}
            className="input-field text-sm"
            data-testid="audit-success-filter"
          >
            <option value="">Wszystkie statusy</option>
            <option value="true">Tylko sukcesy</option>
            <option value="false">Tylko błędy</option>
          </select>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="input-field text-sm"
            data-testid="audit-limit-filter"
          >
            <option value="100">100 ostatnich</option>
            <option value="200">200 ostatnich</option>
            <option value="500">500 ostatnich</option>
            <option value="1000">1000 ostatnich</option>
          </select>
          <button
            onClick={fetchLog}
            className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-sm"
            data-testid="audit-refresh-btn"
          >
            Odśwież
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-12 flex items-center justify-center text-slate-400">
            <CircleNotch className="w-6 h-6 animate-spin" />
          </div>
        ) : records.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            Brak wpisów dla wybranych filtrów.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider whitespace-nowrap">
                    Data / Czas
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">
                    Użytkownik
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">
                    Akcja
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">
                    Zasób
                  </th>
                  <th className="text-center py-3 px-4 text-xs font-bold text-slate-600 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedRecords.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`audit-row-${r.id}`}
                  >
                    <td className="py-3 px-4 text-sm text-slate-700 tabular-nums whitespace-nowrap">
                      {formatTime(r.created_at)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="text-sm font-semibold text-slate-800">
                            {r.user_name || r.user_login || (r.user_role === "anonymous" ? "—" : "—")}
                          </div>
                          <span
                            className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                              ROLE_BADGE[r.user_role] || "bg-slate-100 text-slate-600"
                            }`}
                            title={r.user_role === "anonymous"
                              ? "Akcja wykonana bez aktywnej sesji (np. wygasły token, próba logowania, lub wywołanie z urządzenia mobilnego po wylogowaniu)"
                              : undefined}
                          >
                            {r.user_role === "anonymous" ? "anonim" : (r.user_role || "anonim")}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold mr-2 ${
                          METHOD_BADGE[r.method] || "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {r.method}
                      </span>
                      <span className="text-sm text-slate-700">
                        {r.action_label}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm font-medium text-slate-800">
                        {r.resource || "—"}
                      </div>
                      {r.resource_id && (
                        <div className="text-[11px] text-slate-400 font-mono">
                          {r.resource_id.slice(0, 13)}…
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {r.success ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 text-sm font-semibold">
                          <CheckCircle className="w-4 h-4" weight="fill" />
                          {r.status_code}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-600 text-sm font-semibold">
                          <XCircle className="w-4 h-4" weight="fill" />
                          {r.status_code}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={records.length}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              testId="audit-log-pagination"
            />
          </div>
        )}
      </div>
    </div>
  );
}
